import { Routes, Route, Link, Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Home } from './pages/home';
import { ProjectWizard } from './pages/project-wizard';
import { ProjectDetail } from './pages/project-detail';
import { AssetForm } from './pages/asset-form';
import { AssetRiskView } from './pages/asset-risk-view';
import { Results } from './pages/results';
import { BulkCreateAssets } from './pages/bulk-create-assets';
import { Admin } from './pages/admin';
import { Login } from './pages/login';
import { ChangePassword } from './pages/change-password';
import { VendorReadinessDetailPage } from './pages/vendor-readiness-detail';
import { VendorFormPage } from './pages/vendor-form';
import { RequireAuth } from './auth/RequireAuth';
import { useAuth } from './auth/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { api } from './api/client';

export function App() {
  return (
    <Routes>
      {/* Public route — login page is accessible without auth. */}
      <Route path="/login" element={<Login />} />

      {/* Public vendor form (Phase 2) — token-authenticated by URL, no session.
          Backend 404s these APIs unless VPR_PUBLIC_FORM_ENABLED='true'. */}
      <Route path="/vendor-form/:token" element={<VendorFormPage />} />

      {/* All other routes require auth. */}
      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        {/* Slice 9.6.2: alias /projects to the Home page (project list).
            The Delete-project flow used to navigate to /projects which
            didn't match any route, so users landed on a blank page. */}
        <Route path="projects" element={<Home />} />
        <Route
          path="projects/new"
          element={
            <RequireAuth anyRole={['admin', 'super_user', 'analyst']}>
              <ProjectWizard />
            </RequireAuth>
          }
        />
        <Route path="projects/:projectId" element={<ProjectDetail />} />
        <Route
          path="projects/:projectId/vendor-readiness/:assessmentId"
          element={<VendorReadinessDetailPage />}
        />
        <Route
          path="projects/:projectId/assets/new"
          element={
            <RequireAuth anyRole={['admin', 'super_user', 'analyst']}>
              <AssetForm />
            </RequireAuth>
          }
        />
        <Route
          path="projects/:projectId/assets/:assetId/edit"
          element={
            <RequireAuth anyRole={['admin', 'super_user', 'analyst']}>
              <AssetForm />
            </RequireAuth>
          }
        />
        <Route
          path="projects/:projectId/assets/bulk-create"
          element={
            <RequireAuth anyRole={['admin', 'super_user', 'analyst']}>
              <BulkCreateAssets />
            </RequireAuth>
          }
        />
        {/* Read-only asset risk view; every project reader may open it (C-034). */}
        <Route path="projects/:projectId/assets/:assetId" element={<AssetRiskView />} />
        {/* Slice 6b: AssetGrid links here directly with ?project= query. */}
        <Route
          path="assets/:assetId/edit"
          element={
            <RequireAuth anyRole={['admin', 'super_user', 'analyst']}>
              <AssetForm />
            </RequireAuth>
          }
        />
        <Route path="runs/:runId/results" element={<Results />} />
        <Route path="change-password" element={<ChangePassword />} />

        <Route
          path="admin"
          element={
            <RequireAuth anyRole={['admin', 'super_user']}>
              <Admin />
            </RequireAuth>
          }
        />
      </Route>
    </Routes>
  );
}

function Shell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, roles, logout, hasRole } = useAuth();
  const systemInfo = useQuery({
    queryKey: ['system-info'],
    queryFn: () => api.get<{ version: string }>('/system/info'),
    staleTime: 5 * 60_000,
  });
  // Wide pages use a flex-col scroll-less shell so they can manage their
  // own internal virtualised scroll (AssetGrid) or overflow-y-auto tabs.
  // Admin is NOT wide — it is a content page that needs the shell to scroll.
  // isWide = pages that manage their own internal scroll.
  // IMPORTANT: /projects/new must NOT be wide — "new" is the wizard, not a project ID.
  // Only actual project detail pages (UUID paths) are wide.
  const projectDetailPattern = /^\/projects\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/?$/i;
  const bulkCreatePattern    = /^\/projects\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/assets\/bulk-create\/?$/i;
  const resultsPattern       = /^\/runs\/[^/]+\/results\/?$/;
  const isWide = projectDetailPattern.test(location.pathname)
              || bulkCreatePattern.test(location.pathname)
              || resultsPattern.test(location.pathname);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <header className="border-b border-ink-200 bg-white">
        <div className={`${isWide ? 'max-w-none px-6' : 'max-w-6xl mx-auto px-6'} py-4 flex items-baseline justify-between`}>
          <Link to="/" className="font-display text-2xl text-ink">
            QRAM
            <span className="text-ink-300 italic ml-2 text-base font-normal">
              Quantum Risk Assessment
            </span>
          </Link>
          <nav className="text-sm flex items-baseline gap-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-3 py-1 ${isActive ? 'text-accent font-medium' : 'text-ink-500 hover:text-ink-900'}`
              }
            >
              Projects
            </NavLink>
            {/* Slice 9.6: Admin link only shown to users with admin role. */}
            {(hasRole('admin') || hasRole('super_user')) && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `px-3 py-1 ${isActive ? 'text-accent font-medium' : 'text-ink-500 hover:text-ink-900'}`
                }
              >
                Admin
              </NavLink>
            )}
            {user && (
              <span className="ml-4 pl-4 border-l border-ink-200 text-ink-500 flex items-baseline gap-3">
                <span title={`Roles: ${roles.join(', ') || 'none'}`}>
                  <span className="text-ink-700">{user.display_name}</span>
                  <span className="ml-2 text-xs text-ink-400">({roles.join('/') || 'no role'})</span>
                </span>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="text-xs text-ink-500 hover:text-accent underline"
                >
                  Sign out
                </button>
              </span>
            )}
          </nav>
        </div>
      </header>
      <main
        className={isWide ? 'flex-1 flex flex-col min-h-0' : 'flex-1'}
        style={isWide ? undefined : { overflowY: 'auto' }}
      >
        <Outlet />
      </main>
      {!isWide && (
        <footer className="border-t border-ink-200 py-4 text-center text-xs text-ink-300" data-testid="app-footer">
          {/* Product version from the build (C-021/C-022); no internal build or sprint labels. */}
          QRAM — Quantum Risk Assessment and Management{systemInfo.data?.version ? ` · v${systemInfo.data.version}` : ''}
        </footer>
      )}
    </div>
  );
}